import { Box, BoxProps, Image } from '../../../../src'
import { useScrollPosition } from '@n8tb1t/use-scroll-position'
import React, { useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import type {
  ReactLottieConfig,
  ReactLottieOwnProps,
  ReactLottiePlayingState
} from '@alfonmga/react-lottie-light-ts'

export enum PlayState {
  PLAYING = 'playing',
  PAUSED = 'paused',
  STOPPED = 'stopped'
}

const Lottie = dynamic<ReactLottieOwnProps>(
  // Lottie typedef for defaultProps.playingState should be of
  // type ReactLottiePlayingState and not string.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  () => {
    return import('@alfonmga/react-lottie-light-ts').then(module => module.Lottie)
  },
  {
    ssr: false
  }
)

interface LottieAnimationProps extends BoxProps {
  animation: string | Record<string, unknown>
  placeholderUrl?: string
  forcePlayState?: ReactLottiePlayingState
  speed?: number
  autoplay?: boolean
  loop?: boolean
  hover?: boolean
  /** `BoxProps` for `<Box>` component that wraps `<Lottie/>` */
  innerBoxProps?: BoxProps
  bounce?: boolean
  reverse?: boolean
  dropShadow?: boolean
  shadow?: boolean
  scroll?: boolean
}

export const LottieAnimation = ({
  animation,
  placeholderUrl,
  forcePlayState,
  speed,
  autoplay,
  loop,
  hover,
  innerBoxProps,
  bounce,
  reverse,
  shadow = false,
  scroll,
  ...rest
}: LottieAnimationProps): JSX.Element => {
  const [buffer, setBuffer] = useState<ReactLottiePlayingState>(autoplay ? 'playing' : 'paused')
  const [isReverse, setIsReverse] = useState(reverse ?? false)
  const [isAnimationLoaded, setIsAnimationLoaded] = useState(typeof animation === 'object')
  const lottieRef = useRef<HTMLDivElement>(null)

  const config: ReactLottieConfig = {
    animationData: typeof animation === 'object' ? animation : undefined,
    path: typeof animation === 'string' ? animation : undefined,
    loop: loop && !bounce,
    autoplay: autoplay,
    renderer: 'svg',
    rendererSettings: {
      progressiveLoad: true,
      preserveAspectRatio: ''
    }
  }

  const isInViewport = (): boolean => {
    const rect = lottieRef.current?.getBoundingClientRect()
    return !rect
      ? false
      : rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  }

  useScrollPosition(() => {
    if (scroll && isInViewport()) {
      setBuffer(PlayState.PLAYING)
    } else {
      setBuffer(PlayState.STOPPED)
    }
  })

  const filterShadow = useMemo(() => {
    if (shadow) {
      if (buffer === 'playing') {
        return 'drop-shadow(3px 5px 2px rgb(0 0 0 / 0.6))'
      } else {
        return 'drop-shadow(3px 5px 2px rgb(0 0 0 / 0.3))'
      }
    }
    return undefined
  }, [shadow, buffer])

  return (
    <Box
      p={0}
      m={0}
      ref={lottieRef}
      filter={filterShadow}
      onMouseEnter={hover ? () => setBuffer(PlayState.PLAYING) : undefined}
      onMouseLeave={hover ? () => setBuffer(PlayState.PAUSED) : undefined}
      {...rest}
    >
      {placeholderUrl && (
        <Image alt="" src={placeholderUrl} display={isAnimationLoaded ? 'none' : 'block'} />
      )}
      <Box display={isAnimationLoaded ? 'block' : 'none'} {...innerBoxProps}>
        <Lottie
          playingState={forcePlayState || buffer}
          config={config}
          speed={speed}
          direction={isReverse ? -1 : 1}
          lottieEventListeners={[
            {
              name: 'data_ready',
              callback: () => {
                setIsAnimationLoaded(true)
              }
            },
            {
              name: 'complete',
              callback: () => {
                if (bounce) {
                  setIsReverse(x => !x)
                }
              }
            }
          ]}
        />
      </Box>
    </Box>
  )
}
